import { MessageSeverity } from "primereact/api";

import { LogoReadModel, WhitelabelApi, WhitelabelSettingReadModel } from "../../../@api";

import { translationKeys } from "../../models/app-translation-keys";
import { ApiRequestCachingStore } from "../../stores/ApiRequestCachingStore";
import { IStoreContext } from "../../stores/StoreContext";
import { WhitelabelApiService } from "../ApiServices/WhitelabelApiService";
import GenericService from "../GenericService";

export class WhitelabelService extends GenericService<WhitelabelApi, WhitelabelApiService> {
  private static readonly logoCacheTtlMs = 60 * 60 * 1000;
  private readonly apiRequestCacheStore: ApiRequestCachingStore;

  constructor(storeContext: IStoreContext) {
    super(storeContext, WhitelabelApi, WhitelabelApiService);
    this.apiRequestCacheStore = storeContext.apiRequestCachingStore;
  }

  public async getSettingImage(languageCode: string): Promise<Blob> {
    return this.apiRequestCacheStore.getCachedOrRequest<Blob>(
      `logo-${languageCode.toLowerCase()}`,
      () =>
        this.apiService.whitelabelGetLogo(
          { languageCode: languageCode.toLowerCase() },
          { cache: "no-store" }, // bypass browser HTTP cache; app-level cache handles TTL
        ),
      WhitelabelService.logoCacheTtlMs,
    );
  }

  public async getLogos(): Promise<Record<string, LogoReadModel>> {
    return this.apiService.whitelabelGetLogos();
  }

  public async updateImageSetting(languageCode: string, file: File): Promise<void> {
    await this.apiService.whitelabelUpdateLogoPut({
      languageCode,
      logo: file,
    });

    this.apiRequestCacheStore.remove(`logo-${languageCode.toLowerCase()}`);
    this.storeContext.whitelabelStore.notifyLogoChanged();
    this.showAutoSaveToast();
  }

  public async resetImageSetting(languageCode: string): Promise<void> {
    await this.apiService.whitelabelResetLogoToDefaultPut({
      languageCode,
    });

    this.apiRequestCacheStore.remove(`logo-${languageCode.toLowerCase()}`);
    this.storeContext.whitelabelStore.notifyLogoChanged();
    this.showAutoSaveToast();
  }

  public async getGeneralSettings(): Promise<WhitelabelSettingReadModel[]> {
    const settingsList = await this.apiService.whitelabelGetSettings();

    this.storeContext.whitelabelStore.whitelabelSettings = settingsList;

    return settingsList;
  }

  public async getFilteredSettings(category: string): Promise<WhitelabelSettingReadModel[]> {
    const settingsList = await this.getGeneralSettings();

    return settingsList.filter(setting => setting.key.startsWith(category));
  }

  public async updateSetting(key: string, value: string): Promise<void> {
    await this.apiService.whitelabelUpdateSettingPut({
      whitelabelSettingWriteModel: {
        rowKey: key,
        value,
      },
    });

    this.updateSettingInStore(key, value);

    this.showAutoSaveToast();
  }

  public async resetAllSettingsToDefault(): Promise<void> {
    await this.apiService.whitelabelResetAllSettingsToDefaultPut();

    await this.getGeneralSettings();

    this.showAutoSaveToast();
  }

  public async resetSettingsToDefault(key: string): Promise<void> {
    await this.apiService.whitelabelResetSettingToDefaultPut({ key });

    await this.getGeneralSettings();

    this.showAutoSaveToast();
  }

  private updateSettingInStore(key: string, value: string): void {
    const whitelabelStore = this.storeContext.whitelabelStore;

    if (!whitelabelStore.whitelabelSettings) {
      return;
    }

    whitelabelStore.whitelabelSettings = whitelabelStore.whitelabelSettings.map(oldSetting =>
      oldSetting.key === key
        ? {
            key,
            defaultValue: oldSetting.defaultValue,
            customValue: value,
          }
        : oldSetting,
    );
  }

  private showAutoSaveToast(): void {
    this.storeContext.toastStore.addThrottledToast(
      {
        severity: MessageSeverity.SUCCESS,
        summary: {
          translationKey: translationKeys.toast.autoSave.summary,
        },
        detail: {
          translationKey: translationKeys.toast.autoSave.detail,
        },
        position: "top-right",
        life: 3000,
        replace: true,
      },
      1000,
    );
  }
}
